/**
 * Authentication utility functions for JWT token handling
 */

import { getApiUrl } from './config';
import { ACTIVITY_STORAGE_KEY } from './hooks/useIdleTimeout';
import logger from './utils/logger';

// ── Token refresh internals ──────────────────────────────────────────
let _refreshTimerId = null;
let _refreshPromise = null; // dedup concurrent refresh attempts

// Maximum time to wait for the backend logout request before giving up
// and clearing local auth state anyway. Prevents a hung fetch (e.g. after
// laptop wake / idle timeout) from blocking the redirect to /login.
const LOGOUT_REQUEST_TIMEOUT_MS = 5000;

/**
 * Decode the JWT payload without verifying the signature.
 * Returns null for malformed tokens.
 */
function decodePayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

/**
 * Return the expiry time (epoch seconds) of the current token, or null.
 */
export function getTokenExpiry() {
  const token = localStorage.getItem('auth_token');
  if (!token) return null;
  const payload = decodePayload(token);
  return payload?.exp ?? null;
}

/**
 * Call the backend refresh-token endpoint.
 * On success the new token is persisted and the proactive timer is rescheduled.
 * Returns the new token string, or null on failure.
 */
export async function refreshToken() {
  // Deduplicate: if a refresh is already in flight, piggyback on it
  if (_refreshPromise) return _refreshPromise;

  const token = localStorage.getItem('auth_token');
  if (!token) return null;

  _refreshPromise = (async () => {
    try {
      const res = await fetch(getApiUrl('/api/auth/refresh-token/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) return null;

      const data = await res.json();
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
        scheduleTokenRefresh();
        return data.token;
      }
      return null;
    } catch (err) {
      logger.error('Token refresh failed:', err);
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
}

/**
 * Schedule a proactive refresh so the token is renewed before it expires.
 * The refresh fires when 75 % of the token's lifetime has elapsed
 * (i.e. with 25 % of the lifetime still remaining).
 */
export function scheduleTokenRefresh() {
  // Clear any previously scheduled timer
  if (_refreshTimerId) {
    clearTimeout(_refreshTimerId);
    _refreshTimerId = null;
  }

  const token = localStorage.getItem('auth_token');
  if (!token) return;

  const payload = decodePayload(token);
  if (!payload?.exp || !payload?.iat) return;

  const nowSec = Math.floor(Date.now() / 1000);
  const lifetime = payload.exp - payload.iat;
  // Refresh when 75% of the lifetime has passed
  const refreshAt = payload.iat + Math.floor(lifetime * 0.75);
  const delayMs = Math.max((refreshAt - nowSec) * 1000, 0);

  _refreshTimerId = setTimeout(() => {
    refreshToken();
  }, delayMs);
}

/**
 * Stop any scheduled proactive refresh (e.g. on logout).
 */
export function cancelTokenRefresh() {
  if (_refreshTimerId) {
    clearTimeout(_refreshTimerId);
    _refreshTimerId = null;
  }
}

/**
 * Get the current user's data from the JWT token stored in localStorage
 * @returns {Object|null} User object with {user_id, email, username, exp, iat} or null if no valid token
 */
export function getCurrentUser() {
  const token = localStorage.getItem('auth_token');

  if (!token) {
    return null;
  }

  try {
    // Decode JWT payload (middle part)
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = JSON.parse(atob(parts[1]));

    // Check if token is expired
    if (payload.exp) {
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        // Token expired, clear it
        localStorage.removeItem('auth_token');
        return null;
      }
    }

    return {
      user_id: payload.user_id,
      email: payload.email,
      username: payload.username,
      roles: payload.roles || [],
      org_type: payload.org_type ?? null,
      display_rules: payload.display_rules ?? {},
      exp: payload.exp,
      iat: payload.iat,
    };
  } catch (error) {
    logger.error('Failed to decode token:', error);
    return null;
  }
}

/**
 * Get the authentication token from localStorage
 * @returns {string|null} JWT token or null if not found
 */
export function getAuthToken() {
  return localStorage.getItem('auth_token');
}

/**
 * Check if user is currently authenticated
 * @returns {boolean} True if valid token exists, false otherwise
 */
export function isAuthenticated() {
  return getCurrentUser() !== null;
}

/**
 * Logout the current user.
 *
 * Clears the Django session via the backend and removes all auth data from
 * localStorage.  The Django session must be cleared so that stale sso_org_id
 * state cannot interfere with the next login's OrgAwareDjangoStrategy lookup,
 * which was the root cause of intermittent "Invalid or expired auth code" errors.
 *
 * Returns a Promise so callers can await it, though in practice the function
 * ends with a full-page redirect and the promise never resolves in that tab.
 */
export async function logout() {
  cancelTokenRefresh();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), LOGOUT_REQUEST_TIMEOUT_MS);
  // Default to the local login route. The backend tells us where to go next:
  // for SSO users it returns the IdP-initiated logout URL (Entra), for
  // password-only users it returns null and we stay on /login.
  let logoutUrl = '/login';

  try {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(getApiUrl('/api/auth/logout/'), {
      method: 'POST',
      headers,
      signal: controller.signal,
    });
    // Tolerate malformed/empty bodies — fall back to /login in those cases.
    const data = await response.json().catch(() => ({}));
    if (data && data.logout_url) {
      logoutUrl = data.logout_url;
    }
  } catch (err) {
    logger.error('Backend logout request failed:', err);
  } finally {
    clearTimeout(timeoutId);
  }

  clearAuthData();
  cancelTokenRefresh();

  window.location.href = logoutUrl;
}

/**
 * Get user's email from token
 * @returns {string|null} User's email or null
 */
export function getUserEmail() {
  const user = getCurrentUser();
  return user ? user.email : null;
}

/**
 * Get user's username from token
 * @returns {string|null} User's username or null
 */
export function getUserUsername() {
  const user = getCurrentUser();
  return user ? user.username : null;
}

/**
 * Get user's ID from token
 * @returns {number|null} User's ID or null
 */
export function getUserId() {
  const user = getCurrentUser();
  return user ? user.user_id : null;
}

/**
 * Get the current user's organization name from the JWT token.
 * Uses roles[0].organization as the canonical active org name.
 * @returns {string|null} Organization display name or null
 */
export function getUserOrganizationName() {
  const user = getCurrentUser();
  if (!user || !user.roles || user.roles.length === 0) return null;
  return user.roles[0]?.organization ?? null;
}

/**
 * Get the current user's org_type from the JWT token.
 * @returns {string|null} One of "INTERNAL", "CARRIER", "CLIENT", "TPA", or null
 */
export function getOrgType() {
  const user = getCurrentUser();
  return user ? user.org_type : null;
}

/**
 * Check if a token exists in localStorage (regardless of validity)
 * @returns {boolean} True if token exists, false otherwise
 */
export function hasToken() {
  return localStorage.getItem('auth_token') !== null;
}

/**
 * Clear all authentication data from localStorage
 * This is useful when you want to ensure a clean state before login
 */
export function clearAuthData() {
  cancelTokenRefresh();
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_email');
  localStorage.removeItem(ACTIVITY_STORAGE_KEY);
}

/**
 * Validate and clean up expired or invalid tokens
 * Returns true if a valid token exists, false otherwise
 * Automatically removes invalid/expired tokens from localStorage
 * @returns {boolean} True if valid token exists, false if no token or invalid/expired
 */
export function validateAndCleanToken() {
  const token = localStorage.getItem('auth_token');

  if (!token) {
    return false;
  }

  try {
    // Decode JWT payload (middle part)
    const parts = token.split('.');
    if (parts.length !== 3) {
      // Invalid token structure, clear it
      clearAuthData();
      return false;
    }

    const payload = JSON.parse(atob(parts[1]));

    // Check if token is expired
    if (payload.exp) {
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        // Token expired, clear it
        clearAuthData();
        return false;
      }
    }

    // Token is valid
    return true;
  } catch (error) {
    // Failed to decode token, clearing:
    logger.error('Failed to decode token, clearing:', error);
    clearAuthData();
    return false;
  }
}

/**
 * Check if user is a SmartLight employee
 * @returns {boolean} True if user is internal employee, false otherwise
 */
export function isSmartLightEmployee() {
  return getOrgType() === 'INTERNAL';
}

/**
 * Get the active organization id for the current user.
 *
 * Reads roles[0].organization_id from the decoded JWT — the token already
 * carries this (see backend organizations/services/token_service.py). The
 * first role is the canonical active org (matches getOrgType()'s convention).
 *
 * @returns {number|null} Integer PK of the user's active org, or null
 */
export function getUserOrganizationId() {
  const user = getCurrentUser();
  if (!user || !user.roles || user.roles.length === 0) return null;
  return user.roles[0]?.organization_id ?? null;
}

/**
 * Return the default landing route for a user based on their org_type.
 * @param {Object|null} user - User object from getCurrentUser(), or null.
 * @returns {string} A route path string.
 */
export function getDefaultRoute(user) {
  if (!user) return '/login';
  switch (user.org_type) {
  case 'CARRIER':
    return '/referrals';
  case 'INTERNAL':
    return '/carrier-engagement-dashboard';
  // TODO: CLIENT and TPA have dedicated routes planned — update when those are built
  case 'CLIENT':
  case 'TPA':
    return '/referrals';
  default:
    return '/login';
  }
}
